/**
 * BookAclassic Design Tokens & Rules
 *
 * Brand green palette — single source of truth for every green shade used
 * across the product. Component, page, and lib files MUST NOT contain raw
 * green hex/rgba literals; they import from this module, or use the
 * Tailwind utilities backed by the corresponding CSS variables defined in
 * `index.css` (`--color-brand`, `--color-brand-hover`, `--color-brand-light`,
 * `--color-brand-bright`, `--color-brand-deep`, `--color-brand-night`,
 * `--color-brand-mid`, `--color-brand-divider`, `--color-brand-tint`,
 * `--color-brand-dark-ink`, `--color-brand-dm`, and the
 * `--color-brand-primary-action*` tokens).
 *
 * Canonical brand green: BRAND_GREEN (#1B4332). Mirrored as the shadcn
 * `--primary` HSL variable (153 42% 18%). Deprecated shades that previously
 * coexisted (`#1B3A2D`, `#0B6B3A`) have been reconciled to BRAND_GREEN; do
 * not reintroduce them.
 *
 * Motion: Framer Motion transitions use the house spring tokens in
 * `src/lib/motion.ts` (`HOUSE_SPRING` — spring, bounce 0, ~0.35s) with
 * reduced-motion fallbacks; do not add new fixed tween+delay stacks.
 *
 * Section backgrounds:
 *   - White:      SECTION_BG_WHITE
 *   - Off-white:  OFF_WHITE
 *   - Dark green: BRAND_GREEN (list-your-vehicle CTA, premium accents)
 *
 * Card hover:
 *   - Image scale 1.02, 0.3s ease (image only, not the card itself)
 *
 * Button styles:
 *   - Primary:   BRAND_GREEN bg, white text, 8px border-radius
 *   - Secondary: transparent bg, BRAND_GREEN border, 8px border-radius
 *
 * Typography:
 *   - Sentence-case headings (not ALL CAPS)
 *   - Body copy max-width: 640px
 *
 * Mobile breakpoints:
 *   - Single-column layout below 640px
 *   - Hamburger menu below 768px
 *
 * Navbar:
 *   - Homepage: transparent with white text at top, solid white after 60px scroll
 *   - All other pages: solid white with dark text
 */

// Canonical brand green and its tonal companions. These are the ONLY place
// green hex/rgba literals are allowed to appear in the codebase.
export const BRAND_GREEN          = "#1B4332"; // canonical primary
export const BRAND_GREEN_HOVER    = "#143528"; // hover/pressed state on brand surfaces
export const BRAND_GREEN_DARK_INK = "#143026"; // alt darker hover (consent banner)
export const BRAND_GREEN_LIGHT    = "#2d6a4f"; // medium green (hover gradient stop)
export const BRAND_GREEN_MID      = "#2d5a47"; // mid hover green (conversation header)
export const BRAND_GREEN_BRIGHT   = "#40916c"; // brighter accent (gradient stop)
export const BRAND_GREEN_DEEP     = "#0F2318"; // very dark gradient stop
export const BRAND_GREEN_NIGHT    = "#0A1A12"; // near-black gradient stop
export const BRAND_GREEN_DIVIDER  = "#2E5C42"; // hairline divider on brand surfaces
export const BRAND_GREEN_TINT     = "#e8f0ec"; // pale brand-tinted background
export const BRAND_GREEN_DM       = "#a3c9b0"; // dark-mode brand replacement
export const BRAND_GREEN_DM_TEXT  = "#52B788"; // dark-mode brand text accent
export const PRIMARY_ACTION_BACKGROUND = BRAND_GREEN;
export const PRIMARY_ACTION_FOREGROUND = "#FFFFFF";
export const PRIMARY_ACTION_HOVER = BRAND_GREEN_HOVER;
export const APEX_ELITE_DARK_TEXT = "#D5EAD9";
export const APEX_ELITE_DARK_BG = "#1C382B";
export const APEX_ELITE_DARK_BORDER = "#6B9A7A";

export const BRAND_GREEN_SOFT_07  = "rgba(27, 67, 50, 0.07)";
export const BRAND_GREEN_SOFT_08  = "rgba(27, 67, 50, 0.08)";
export const BRAND_GREEN_SOFT_10  = "rgba(27, 67, 50, 0.10)";
export const BRAND_GREEN_SOFT_15  = "rgba(27, 67, 50, 0.15)";

// Celebration confetti palette — distinct decorative greens used only by the
// listing-moderation success animation. Defined here so confetti palette
// tweaks happen in one place rather than as inline literals.
export const CELEBRATION_PALETTE: readonly string[] = [
  BRAND_GREEN, BRAND_GREEN_LIGHT, BRAND_GREEN_BRIGHT, "#52b788", "#74c69d",
  "#95d5b2", "#b7e4c7", "#d4af37", "#f0c040", "#e8c547",
];

export const OFF_WHITE = "#FAF8F4";
export const SECTION_BG_WHITE = "#FFFFFF";

export const NAVBAR_SCROLL_THRESHOLD = 60;

export const CARD_HOVER_SCALE = 1.02;
export const CARD_HOVER_DURATION = "0.3s";

export const BUTTON_RADIUS = "8px";

export const BODY_COPY_MAX_WIDTH = "640px";

export const MOBILE_BREAKPOINT = 640;
export const HAMBURGER_BREAKPOINT = 768;

export const MATERIAL_CHIP_BLUR = "4px";

export const MATERIAL_PANEL_BLUR = "8px";

export const MATERIAL_SCRIM_BLUR = "2px";

export const MATERIAL_CHIP_ALPHA = 0.9;

export const MATERIAL_SCRIM_DIM_ALPHA = 0.5;

export const MATERIAL_PANEL_ALPHA = 0.85;
